import { GetStaticPaths, GetStaticProps } from 'next';

import {
  ProductDetailsDocument,
  ProductPathsDocument,
} from '@sdk/gqlTypes/generatedHooks';
import {
  ProductDetailsQuery,
  ProductDetailsQueryVariables,
  ProductPathsQuery,
  ProductPathsQueryVariables,
} from '@sdk/gqlTypes/generatedOperations';

import { PrismicProductPageQueryDocument } from '@prismic/gqlTypes/prismicHooks';

import {
  PrismicProductPageQuery,
  PrismicProductPageQueryVariables,
} from '@prismic/gqlTypes/prismicOperations';

import {
  incrementalStaticRegenerationRevalidate,
  staticPathsFallback,
} from '@temp/constants';

import { prismicApolloClient, saleorApolloClient } from '@utils/ssg';
import { localeToPrismicQuery, localeToSaleorQuery } from '@utils/regions';

import { generateProductSeo } from '@utils/seo';

import { decodeKey, generateKey } from '@utils/core';

import { ProductPage, ProductPageProps } from '@temp/views/Product';
import { CONFIGURATOR_PRODUCT_TYPES } from '@temp/views/Product/types';

export default ProductPage;

export const getStaticPaths: GetStaticPaths<any> = async ({ locales }) => {
  const { data } = await saleorApolloClient.query<
    ProductPathsQuery,
    ProductPathsQueryVariables
  >({
    query: ProductPathsDocument,
    // variables: { uid: slug, },
  });

  const paths = [];

  locales.forEach((locale) => {
    const localPaths = data.products.edges.map(({ node }) => ({
      params: {
        slug: node.name,
        id: decodeKey(node.id, 'Product'),
      },
      locale,
    }));
    paths.push(...localPaths);
  });

  return { paths, fallback: staticPathsFallback };
};

export const getStaticProps: GetStaticProps<
  ProductPageProps,
  ProductPageProps['params']
> = async ({ params: { slug, id }, locale, defaultLocale, locales }) => {
  const { data: dataSaleor } = await saleorApolloClient.query<
    ProductDetailsQuery,
    ProductDetailsQueryVariables
  >({
    query: ProductDetailsDocument,
    variables: {
      id: generateKey(id, 'Product'),
      ...localeToSaleorQuery(locale),
    },
  });

  const { product } = dataSaleor || {};

  if (!product) {
    return {
      notFound: true,
    };
  }

  const isConfiguratorType = CONFIGURATOR_PRODUCT_TYPES.includes(
    product.productType.slug
  );

  const { data: dataPrismic } = await prismicApolloClient.query<
    PrismicProductPageQuery,
    PrismicProductPageQueryVariables
  >({
    query: PrismicProductPageQueryDocument,
    variables: {
      productUid: isConfiguratorType ? product.productType.slug : product.slug,
      ...localeToPrismicQuery(locale),
    },
  });

  const { product: productDetails } = dataPrismic || {};

  const seo = await generateProductSeo(product, locale, defaultLocale, locales);

  return {
    revalidate: incrementalStaticRegenerationRevalidate,
    props: {
      product,
      productDetails,
      seo,
      params: { slug, id },
    },
  };
};
